'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ClickArea from '@haaretz/s-click-area';
import FormfieldDescription from '@haaretz/s-formfield-description';
import Icon from '@haaretz/s-icon';
import React, { useCallback, useEffect, useId, useImperativeHandle } from 'react';
import s9 from 'style9';
const borderWidth = '1px';
const transitionDuration = '0.2s';
const c = s9.create({
    base: {
        // This uses the "space toggle" technique.
        // See https://tinyurl.com/css-space-toggle for how this works.
        '--show-bg': 'var(--OFF)',
        '--inp-bgc-fallback': 'transparent',
        // Since off above is just a whitespace, the value we assign to
        // `--dflt-*-bgc` ends up being transparent. if we switch
        // `--show-bg` to `--ON`, the value will be `initial transparent`,
        // which effectively, is the same as `initial`, which basically
        // tells the var using `--dflt-*-bgc` to ignore it and use the
        // fallback value.
        // For a more thorough explanation see here:
        // https://tinyurl.com/space-toggle-logic
        '--dflt-inp-bgc': 'var(--show-bg) var(--inp-bgc-fallback)',
        '--dflt-lbl-bgc': 'var(--dflt-inp-bgc)',
        '--_inp-bgc': 'var(--dflt-inp-bgc, var(--inp-bgc, var(--inp-bgc-fallback)))',
        '--_lbl-bgc': 'var(--dflt-lbl-bgc, var(--lbl-bgc, var(--inp-bgc, var(--inp-bgc-fallback))))',
        '--_inp-brdr-rad': `var(--inp-brdr-rad, var(--inp-dflt-brdr-rad, ${"clamp(0px, ((100vw - 4px) - 100%) * 9999, 0.1875rem) 0.1875rem"}))`,
        '--_inp-pdng-blk': `var(--inp-pdng-blk, var(--inp-dflt-pdng-blk, ${"0.375rem"}))`,
        '--_inp-pdng-inln': `var(--inp-pdng-inln, var(--inp-dflt-pdng-inln, ${"0.5rem"}))`,
        '--_inp-pdng-inln-with-icn': `var(--inp-pdng-inln-with-icn, var(--inp-dflt-pdng-inln-with-icn, ${"2rem"}))`,
        '--_inp-fs': `var(--inp-fs, var(--inp-dflt-fs, ${"var(--fz-0)"}))`,
        '--_inp-lh': `var(--inp-lh, var(--inp-dflt-lh, ${"var(--lh-0)"}))`,
        '--_lbl-fs': `var(--lbl-fs, var(--lbl-dflt-fs, ${"var(--fz--1)"}))`,
        '--_lbl-lh': `var(--lbl-lh, var(--lbl-dflt-lh, ${"var(--lh--1)"}))`,
        '--_lbl-rsd-fs': `var(--lbl-rsd-fs, var(--lbl-rsd-dflt-fs, ${"var(--fz--2)"}))`,
        '--_lbl-rsd-lh': `var(--lbl-rsd-lh, var(--lbl-rsd-dflt-lh, ${"var(--lh--2)"}))`,
        '--_lbl-rsd-top': 'var(--lbl-rsd-top, var(--lbl-rsd-dflt-top, -100%))',
        '--_lbl-rsd-pdng-inln': `var(--lbl-rsd-pdng-inln, var(--lbl-rsd-dflt-pdng-inln, ${"0.25rem"}))`,
        '--_lbl-pdng-inln-wth-strt-icn': `var(--lbl-pdng-inln-wth-strt-icn, var(--lbl-dflt-pdng-inln-wth-strt-icn, ${"1.5rem"}))`,
        '--_rqrd-fs': `var(--rqrd-fs, var(--rqrd-dflt-fs, ${"var(--fz-0)"}))`,
        '--rqrd-lh': `var(--rqrd-lh, var(--rqrd-dflt-lh, ${"var(--lh-0)"}))`,
        '--_rqrd-rsd-fs': `var(--rqrd-rsd-fs, var(--rqrd-rsd-dflt-fs, ${"var(--fz--2)"}))`,
        '--_rqrd-rsd-lh': `var(--rqrd-rsd-lh, var(--rqrd-rsd-dflt-lh, ${"var(--lh--2)"}))`,
        '--_icn-sz': `var(--icn-sz, var(--icn-dflt-sz, ${"1.125rem"}))`,
        '--_icn-inst-inln': `var(--icn-inst-inln, var(--icn-dflt-inst-inln, ${"0.5rem"}))`,
        '--_shw-pswd-icn-sz': `var(--shw-pswd-icn-sz, var(--shw-pswd-icn-dflt-sz, ${"1.75rem"}))`,
        '--_strt-part-wdth': `var(--strt-part-wdth, var(--strt-part-dflt-wdth, ${"0.5rem"}))`,
        display: 'inline-flex',
        flexWrap: 'wrap',
        fontFamily: 'var(--font-brand),"Helvetica Neue",Helvetica,arial,tahoma,sans-serif',
        "@media all and (max-width:63.9375em)": {
            "--icn-dflt-sz": "1rem",
            "--shw-pswd-icn-dflt-sz": "1.5rem"
        },
        "@media all and (min-width:80em) and (max-width:101.9375em)": {
            "--_shw-pswd-icn-dflt-sz": "1.6875rem"
        },
        "@media all and (min-width:80em)": {
            "--rqrd-dflt-fs": "var(--fz--1)",
            "--rqrd-dflt-lh": "var(--lh--1)",
            "--rqrd-rsd-dflt-fs": "var(--fz--3)",
            "--rqrd-rsd-dflt-lh": "var(--lh--3)"
        },
        "@media all and (min-width:102em)": {
            "--inp-dflt-fs": "var(--fz--1)",
            "--inp-dflt-lh": "var(--lh--1)",
            "--inp-dflt-pdng-inln-with-icn": "2.25rem",
            "--lbl-dflt-fs": "var(--fz--1)",
            "--lbl-dflt-lh": "var(--lh--1)",
            "--lbl-rsd-dflt-fs": "var(--fz--3)",
            "--lbl-rsd-dflt-lh": "var(--lh--3)",
            "--lbl-dflt-pdng-inln-wth-strt-icn": "1.75rem",
            "--shw-pswd-dflt-icn-sz": "1.75rem",
            "--icn-dflt-sz": "1.3125rem",
            "--_shw-pswd-icn-dflt-sz": "2rem"
        },
        ':hover': {
            '--inp-brdr-c': 'var(--inp-hvr-brdr-c)'
        },
        ':focus-within': {
            '--inp-brdr-c': 'var(--inp-hvr-brdr-c)'
        }
    },
    opaque: {
        // This makes `--_inp-bgc` use the value of `--inp-bgc`
        // instead of `--default-inp-bgc`.
        // (and same for label)
        '--show-bg': 'var(--ON)'
    },
    borderTransition: {
        transitionProperty: 'border-color',
        transitionDuration,
        transitionTimingFunction: 'ease-in-out'
    },
    disabled: {
        cursor: 'no-drop'
    },
    input: {
        backgroundColor: 'var(--_inp-bgc)',
        color: 'var(--txt-c)',
        height: '100%',
        paddingBottom: 'var(--_inp-pdng-blk)',
        paddingTop: 'var(--_inp-pdng-blk)',
        paddingInlineStart: 'var(--_inp-pdng-inln)',
        paddingInlineEnd: 'var(--_inp-pdng-inln)',
        width: '100%',
        // Start part
        borderStartStartRadius: 'var(--_inp-brdr-rad)',
        borderEndStartRadius: 'var(--_inp-brdr-rad)',
        // End part
        borderStartEndRadius: 'var(--_inp-brdr-rad)',
        borderEndEndRadius: 'var(--_inp-brdr-rad)',
        fontSize: 'var(--_inp-fs)',
        lineHeight: 'var(--_inp-lh)',
        '::placeholder': {
            transitionProperty: 'color',
            transitionDuration,
            color: 'transparent'
        },
        ':focus': {
            outline: 'none',
            cursor: 'text'
        }
    },
    inputWithNoLabel: {
        '::placeholder': {
            color: 'var(--plchldr-c)'
        }
    },
    inputWithRaisedLabel: {
        '::placeholder': {
            transitionDelay: '0.15s',
            color: 'var(--plchldr-c)'
        }
    },
    inputEmail: {
        textAlign: 'right'
    },
    inputWrapper: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'flex',
        left: 0,
        right: 0,
        pointerEvents: 'none',
        maxWidth: '100%'
    },
    inputWithStartIcon: {
        paddingInlineStart: 'var(--_inp-pdng-inln-with-icn)'
    },
    inputWithEndIcon: {
        paddingInlineEnd: 'var(--_inp-pdng-inln-with-icn)'
    },
    wrapper: {
        display: 'flex',
        width: '100%',
        position: 'relative'
    },
    icon: {
        fontSize: 'var(--_icn-sz)'
    },
    endIcon: {
        color: 'var(--end-icn-c)',
        alignItems: 'center',
        display: 'flex',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        insetInlineEnd: 'var(--_icn-inst-inln)',
        fontSize: 'var(--_icn-sz)'
    },
    showPassword: {
        alignItems: 'center',
        display: 'flex',
        position: 'absolute',
        insetInlineEnd: "0.25rem"
    },
    showPasswordIcon: {
        fontSize: 'var(--_shw-pswd-icn-sz)'
    },
    endPart: {
        flexGrow: 1,
        borderStartEndRadius: 'var(--_inp-brdr-rad)',
        borderEndEndRadius: 'var(--_inp-brdr-rad)',
        borderTopColor: 'var(--inp-brdr-c)',
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        paddingTop: "calc(0.25rem - 1px)",
        borderBottomColor: 'var(--inp-brdr-c)',
        borderBottomWidth: "1px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(0.25rem - 1px)",
        borderInlineEndColor: 'var(--inp-brdr-c)',
        borderInlineEndWidth: "1px",
        borderInlineEndStyle: 'solid',
        paddingInlineEnd: "0.25rem"
    },
    label: {
        backgroundColor: 'var(--_lbl-bgc)',
        color: 'var(--lbl-c)',
        display: 'inline-block',
        padding: 0,
        position: 'relative',
        top: 0,
        transitionProperty: 'all',
        transitionDuration,
        transitionTimingFunction: 'ease-in-out',
        fontSize: 'var(--_lbl-fs)',
        lineHeight: 'var(--_lbl-lh)'
    },
    labelWithStartIcon: {
        insetInlineStart: 'var(--_lbl-pdng-inln-wth-strt-icn)'
    },
    labelRaised: {
        color: 'var(--lbl-rsd-c)',
        fontWeight: 700,
        paddingLeft: 'var(--_lbl-rsd-pdng-inln)',
        paddingRight: 'var(--_lbl-rsd-pdng-inln)',
        transform: 'translateY(50%)',
        top: 'var(--_lbl-rsd-top)',
        insetInlineStart: 0,
        fontSize: 'var(--_lbl-rsd-fs)',
        lineHeight: 'var(--_lbl-rsd-lh)'
    },
    labelWrapper: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        maxWidth: `calc(100% - ${"1rem"} * 2)`,
        width: 'auto',
        borderBottomColor: 'var(--inp-brdr-c)',
        borderBottomWidth: "1px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(0.25rem - 1px)",
        borderTopColor: 'var(--inp-brdr-c)',
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        paddingTop: "calc(0.25rem - 1px)",
        transitionProperty: 'all',
        transitionDuration,
        transitionTimingFunction: 'ease-in-out'
    },
    labelWrapperRaised: {
        borderTopColor: 'transparent'
    },
    required: {
        color: 'var(--inp-rqrd-color)',
        marginInlineStart: "0.25rem",
        transitionProperty: 'top, font-size, line-height',
        transitionDuration: `${transitionDuration}, ${transitionDuration}, ${transitionDuration}`,
        transitionTimingFunction: 'ease-in-out',
        fontSize: 'var(--_rqrd-fs)',
        lineHeight: 'var(--_rqrd-lh)'
    },
    requiredRaised: {
        fontSize: 'var(--_rqrd-rsd-fs)',
        lineHeight: 'var(--_rqrd-rsd-lh)'
    },
    startIcon: {
        alignItems: 'center',
        display: 'flex',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        insetInlineStart: 'var(--_icn-inst-inln)'
    },
    startPart: {
        width: 'var(--_strt-part-wdth)',
        height: '100%',
        borderStartStartRadius: 'var(--_inp-brdr-rad)',
        borderEndStartRadius: 'var(--_inp-brdr-rad)',
        borderTopColor: 'var(--inp-brdr-c)',
        borderTopWidth: "1px",
        borderTopStyle: 'solid',
        paddingTop: "calc(0.25rem - 1px)",
        borderBottomColor: 'var(--inp-brdr-c)',
        borderBottomWidth: "1px",
        borderBottomStyle: 'solid',
        paddingBottom: "calc(0.25rem - 1px)",
        borderInlineStartColor: 'var(--inp-brdr-c)',
        borderInlineStartWidth: "1px",
        borderInlineStartStyle: 'solid',
        paddingInlineStart: "0.25rem"
    },
    defaultState: {},
    disabledState: {
        opacity: 0.25,
        '--inp-brdr-c': 'var(--inp-hvr-brdr-c)'
    },
    isInvalid: {
        '--inp-brdr-c': `var(--inp-err-brdr-c, ${"rgba(var(--c-secondary900),0.2)"})`,
        '--inp-hvr-brdr-c': `var(--inp-err-hvr-brdr-c, ${"rgb(var(--c-secondary900))"})`,
        '--lbl-rsd-c': `var(--lbl-err-c, ${"rgb(var(--c-secondary900))"})`,
        '--end-icn-c': 'var(--lbl-rsd-c)'
    },
    defaultVariant: {
        // input colors
        '--txt-c': "rgb(var(--c-neutral1200))",
        '--inp-brdr-c': "rgba(var(--c-neutral1200),0.2)",
        '--inp-hvr-brdr-c': "rgb(var(--c-neutral1200))",
        '--inp-rqrd-color': "rgb(var(--c-secondary900))",
        '--plchldr-c': "rgb(var(--c-neutral900))",
        // background-color - used only in opaque mode
        '--inp-bgc': "rgb(var(--c-neutral200))",
        // label colors
        // label background-color, falls back to `--inp-bgc` - used only in opaque mode
        // '--lbl-bgc': color('neutral200'),
        '--lbl-c': 'var(--txt-c)',
        '--lbl-rsd-c': "rgb(var(--c-neutral900))"
    },
    inverseVariant: {
        // input colors
        '--txt-c': "rgb(var(--c-neutral150))",
        '--plchldr-c': "rgb(var(--c-neutral400))",
        '--inp-brdr-c': "rgba(var(--c-neutral150),0.2)",
        '--inp-hvr-brdr-c': "rgb(var(--c-neutral150))",
        '--inp-rqrd-color': "rgb(var(--c-secondary500))",
        // background-color - used only in opaque mode
        '--inp-bgc': "rgb(var(--c-neutral800))",
        // label colors
        // label background-color, falls back to `--inp-bgc` - used only in opaque mode
        // '--lbl-bgc': color('neutral200'),
        '--lbl-c': "rgb(var(--c-neutral200))",
        '--lbl-rsd-c': "rgb(var(--c-neutral200))",
        '--lbl-err-c': "rgb(var(--c-secondary500))",
        // Error state
        '--inp-err-brdr-c': "rgba(var(--c-secondary600),0.4)",
        '--inp-err-hvr-brdr-c': "rgb(var(--c-secondary600))"
    },
    topSharp: {
        borderTopLeftRadius: "0",
        borderTopRightRadius: "0"
    },
    bottomSharp: {
        borderBottomLeftRadius: "0",
        borderBottomRightRadius: "0"
    },
    startSharp: {
        borderStartStartRadius: "0",
        borderEndStartRadius: "0",
        borderInlineStartWidth: `var(--inp-brdr-wdth, ${borderWidth})`
    },
    endSharp: {
        borderStartEndRadius: "0",
        borderEndEndRadius: "0",
        borderInlineEndWidth: `var(--inp-brdr-wdth, ${borderWidth})`
    },
    allSharp: {
        borderStartStartRadius: "0",
        borderStartEndRadius: "0",
        borderEndStartRadius: "0",
        borderEndEndRadius: "0"
    },
    topHideBorder: {
        borderTopWidth: 0
    },
    bottomHideBorder: {
        borderBottomWidth: 0
    },
    blockHideBorder: {
        borderBottomWidth: 0,
        borderTopWidth: 0
    },
    startHideBorder: {
        borderInlineStartWidth: 0
    },
    endHideBorder: {
        borderInlineEndWidth: 0
    },
    inlineHideBorder: {
        borderInlineEndWidth: 0,
        borderInlineStartWidth: 0
    },
    allHideBorder: {
        borderTopWidth: 0,
        borderInlineEndWidth: 0,
        borderBottomWidth: 0,
        borderInlineStartWidth: 0
    },
    largeSize: {
        '--inp-dflt-brdr-rad': "clamp(0px, ((100vw - 4px) - 100%) * 9999, 0.75rem) 0.75rem",
        '--inp-dflt-pdng-blk': "0.875rem",
        '--inp-dflt-pdng-inln': "0.875rem",
        '--inp-dflt-pdng-inln-with-icn': "2.5rem",
        '--inp-dflt-fs': "var(--fz-1)",
        '--inp-dflt-lh': "var(--lh-1)",
        '--lbl-dflt-fs': "var(--fz-1)",
        '--lbl-dflt-lh': "var(--lh-1)",
        '--lbl-rsd-dflt-fs': "var(--fz--1)",
        '--lbl-rsd-dflt-lh': "var(--lh--1)",
        '--lbl-rsd-dflt-top': '-90%',
        '--lbl-rsd-dflt-pdng-inln': "1rem",
        '--lbl-dflt-pdng-inln-wth-strt-icn': "2rem",
        '--icn-dflt-sz': "1.75rem",
        '--icn-dflt-inst-inln': "1rem",
        '--shw-pswd-icn-dflt-sz': "2rem",
        '--strt-part-dflt-wdth': "1rem",
        "@media all and (min-width:102em)": {
            "--inp-dflt-fs": "var(--fz-0)",
            "--inp-dflt-lh": "var(--lh-0)",
            "--inp-dflt-pdng-inln-with-icn": "2.75rem",
            "--lbl-dflt-fs": "var(--fz-0)",
            "--lbl-dflt-lh": "var(--lh-0)",
            "--lbl-rsd-dflt-fs": "var(--fz--1)",
            "--lbl-rsd-dflt-lh": "var(--lh--1)",
            "--lbl-dflt-pdng-inln-wth-strt-icn": "2.25rem"
        }
    }
});
const inputTypes = [
    'email',
    'hidden',
    'number',
    'password',
    'search',
    'tel',
    'text'
];
const DEFAULT_WRAPPER_ELEMENT = 'div';
export function TextFieldWrapper({ as, children, inlineStyle, styleExtend = [], state = 'default', isInvalid, variant = 'default', opaque = false, size = 'regular', ...attrs }) {
    const Element = as || DEFAULT_WRAPPER_ELEMENT;
    const variantClass = `${variant}Variant`;
    const stateClass = `${state}State`;
    const hasSize = size !== 'regular';
    return /*#__PURE__*/ _jsx(Element, {
        className: s9(c.base, c[variantClass], c[stateClass], isInvalid && c.isInvalid, opaque && c.opaque, hasSize && c[`${size}Size`], ...styleExtend),
        style: inlineStyle,
        ...attrs,
        children: children
    });
}
export function TextFieldInput({ ref, autoFocus = false, describedBy, hideBorder, endIcon, inlineStyle = {}, label, required = false, sharp, startIcon, state = 'default', isInvalid, styleExtend = [], inputStyleExtend = [], labelStyleExtend = [], type, preRaiseLabel, ...attrs }) {
    const [isAutoFilled, setIsAutoFilled] = React.useState(false);
    const [isLabelRaised, setIsLabelRaised] = React.useState(autoFocus || !!preRaiseLabel);
    const [controlledInputType, setControlledInputType] = React.useState(type);
    const inputId = useId();
    const inputRef = React.useRef(null);
    const containerRef = React.useRef(null);
    useImperativeHandle(ref, ()=>inputRef.current);
    const attrsOverride = {};
    const hasValueInAttrs = useCallback(()=>{
        return !!(attrs.value || attrs.defaultValue || attrsOverride.value || attrsOverride.defaultValue);
    }, [
        attrs.defaultValue,
        attrs.value,
        attrsOverride.defaultValue,
        attrsOverride.value
    ]);
    const checkIsLabelRaised = useCallback(()=>{
        const isFilled = inputRef.current != null ? !!inputRef.current.value : hasValueInAttrs();
        const isRaised = isFilled || isAutoFilled;
        setIsLabelRaised(isRaised);
    }, [
        hasValueInAttrs,
        isAutoFilled
    ]);
    // initial check
    useEffect(()=>{
        if (!autoFocus && !preRaiseLabel) checkIsLabelRaised();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        attrs.defaultValue
    ]);
    useEffect(()=>{
        const selectors = [
            `input[id='${inputId}']:-autofill`,
            `input[id='${inputId}']:-webkit-autofill`
        ].join(',');
        const autofilledElement = containerRef.current?.querySelector(selectors);
        setIsAutoFilled(!!autofilledElement);
    }, [
        inputId
    ]);
    const isDisabled = state === 'disabled';
    if (attrs.value != null && !(attrs.onChange || attrs.readOnly)) {
        attrsOverride.defaultValue = attrs.defaultValue || attrs.value;
        attrsOverride.value = undefined;
    }
    const isPassword = type === 'password';
    const isEmail = type === 'email';
    const isHiddenType = type === 'hidden';
    const sharpClass = sharp ? `${sharp}Sharp` : undefined;
    const cancelBorderClass = hideBorder ? `${hideBorder}HideBorder` : undefined;
    const typeIsValid = controlledInputType && inputTypes.includes(controlledInputType);
    if (!typeIsValid) attrsOverride.type = 'text';
    else if (isEmail) attrsOverride.dir = 'ltr';
    if (describedBy) attrsOverride['aria-describedby'] = describedBy;
    function onBlur(event) {
        if (typeof attrs.onBlur === 'function') attrs.onBlur(event);
        checkIsLabelRaised();
    }
    function onFocus(event) {
        if (typeof attrs.onFocus === 'function') attrs.onFocus(event);
        setIsLabelRaised(true);
    }
    const showPassword = React.useCallback((event)=>{
        event.preventDefault();
        if (isPassword) {
            setControlledInputType((value)=>value === 'password' ? 'text' : 'password');
        }
    }, [
        isPassword
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: s9(c.wrapper, ...styleExtend),
        style: inlineStyle,
        ref: ref,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: s9(c.startIcon, c.borderTransition),
                children: startIcon
            }),
            /*#__PURE__*/ _jsx("div", {
                className: s9(c.endIcon, c.borderTransition),
                children: isPassword ? /*#__PURE__*/ _jsx(ClickArea, {
                    size: "small",
                    rippleSize: "extraSmall",
                    styleExtend: [
                        c.showPassword
                    ],
                    onClick: showPassword,
                    type: "button",
                    children: /*#__PURE__*/ _jsx(Icon, {
                        icon: controlledInputType === 'password' ? 'show' : 'hide',
                        variant: isInvalid ? 'danger' : 'neutral',
                        a11yDescription: 'חשיפת סיסמה',
                        a11yLabel: 'חשיפת סיסמה',
                        styleExtend: [
                            c.showPasswordIcon
                        ]
                    })
                }) : isInvalid ? /*#__PURE__*/ _jsx(Icon, {
                    icon: "warn"
                }) : endIcon
            }),
            /*#__PURE__*/ _jsx("input", {
                className: s9(c.input, !label && c.inputWithNoLabel, !!startIcon && c.inputWithStartIcon, (isPassword || !!endIcon) && c.inputWithEndIcon, isEmail && c.inputEmail, isLabelRaised && c.inputWithRaisedLabel, !!sharpClass && c[sharpClass], ...inputStyleExtend),
                type: controlledInputType,
                id: inputId,
                ref: inputRef,
                required: required,
                ...attrs,
                ...attrsOverride,
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus: autoFocus,
                onBlur: onBlur,
                onFocus: onFocus,
                disabled: isDisabled
            }),
            !isHiddenType ? /*#__PURE__*/ _jsxs("div", {
                className: s9(c.inputWrapper),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: s9(c.startPart, c.borderTransition, !!sharpClass && c[sharpClass], !!cancelBorderClass && c[cancelBorderClass])
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: s9(c.labelWrapper, isLabelRaised && c.labelWrapperRaised, !!cancelBorderClass && c[cancelBorderClass], c.borderTransition),
                        children: label ? /*#__PURE__*/ _jsxs("label", {
                            htmlFor: inputId,
                            className: s9(c.label, !!startIcon && c.labelWithStartIcon, isLabelRaised && c.labelRaised, isDisabled && c.disabled, ...labelStyleExtend),
                            children: [
                                label,
                                required ? /*#__PURE__*/ _jsx("span", {
                                    className: s9(c.required, isLabelRaised && c.requiredRaised),
                                    children: "*"
                                }) : null
                            ]
                        }) : null
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: s9(c.endPart, c.borderTransition, !!sharpClass && c[sharpClass], !!cancelBorderClass && c[cancelBorderClass])
                    })
                ]
            }) : null
        ]
    });
}
export default function TextField({ ref, inlineStyle, styleExtend = [], state = 'default', isInvalid = false, variant = 'default', opaque = false, size = 'regular', ...attrs }) {
    const descriptionId = React.useId();
    const isHiddenType = attrs.type === 'hidden';
    const [inputValue, setInputValue] = React.useState('');
    const description = typeof attrs.description === 'function' ? attrs.description(inputValue) : attrs.description;
    return /*#__PURE__*/ _jsxs(TextFieldWrapper, {
        styleExtend: styleExtend,
        inlineStyle: inlineStyle,
        state: state,
        isInvalid: isInvalid,
        variant: variant,
        opaque: opaque,
        size: size,
        children: [
            /*#__PURE__*/ _jsx(TextFieldInput, {
                ...attrs,
                onInput: (event)=>{
                    if (typeof attrs.description === 'function' && isInvalid) {
                        setInputValue(event.currentTarget.value);
                    }
                    attrs.onInput?.(event);
                },
                onBlur: (event)=>{
                    if (typeof attrs.description === 'function') {
                        setInputValue(event.currentTarget.value);
                    }
                    attrs.onBlur?.(event);
                },
                describedBy: description && descriptionId,
                state: state,
                isInvalid: isInvalid,
                ref: ref
            }),
            description && !isHiddenType ? /*#__PURE__*/ _jsx(FormfieldDescription, {
                variant: variant,
                isInvalid: isInvalid,
                id: descriptionId,
                children: description
            }) : null
        ]
    });
}
const colorCssVariablesHtz = [
    "secondary900",
    "neutral1200",
    "neutral900",
    "neutral200",
    "neutral150",
    "neutral400",
    "secondary500",
    "neutral800",
    "secondary600"
];
const lineHeightCssVariablesHtz = [
    "--lh--1",
    "--lh--3",
    "--lh-0",
    "--lh--2",
    "--lh-1"
];
const fontSizeCssVariablesHtz = [
    "--fz--1",
    "--fz--3",
    "--fz-0",
    "--fz--2",
    "--fz-1"
];

//# sourceMappingURL=TextField.js.map